import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { Order, WorklistItem } from "@probetalab/contracts";
import { get } from "../lib/api.js";
import { fmtDate, fmtTime, money } from "../lib/format.js";
import { useBranch } from "../lib/session.js";
import { orderChips } from "../lib/status.js";
import { useDebounced, useQuery } from "../lib/useQuery.js";
import { Button, Chip, Empty, Loading, Money, PageHead, SearchInput } from "../components/ui.js";
import { DateRangeFilter, type DateRange, dateRangeQuery, describeDateRange, useDateRange } from "../components/DateRangeFilter.js";
import { OrderRowPrintButtons } from "../components/PrintActions.js";
import { Icon } from "../components/icons.js";

type Filter = "all" | "pending_sample" | "in_process" | "to_validate" | "validated" | "delivered" | "balance";

/** The day's orders for the branch, with the chips the counter reads at a glance. */
export function OrdersPage() {
  const branch = useBranch();
  const navigate = useNavigate();
  // Today by default; a date range like Toronja's «Rango de Fecha» (Fernanda, 2026-11-02 20:07).
  const [range, setRange] = useDateRange();
  const dates = dateRangeQuery(range);
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim());
  const [filter, setFilter] = useState<Filter>("all");

  const orders = useQuery(
    // A search looks through every day, as before.
    () => get("orders", { query: { branch_id: branch.id, ...(dq ? {} : dates), q: dq || undefined, limit: 200 } }),
    [branch.id, dates.day, dates.from, dates.to, dq],
  );
  const captured = useQuery(() => get("worklist", { query: { branch_id: branch.id, state: "captured", limit: 200 } }), [branch.id]);
  const toValidate = useMemo(() => new Set((captured.data?.items ?? []).map((w: WorklistItem) => w.order_id)), [captured.data]);

  // Oldest first, the way the counter reads the day (the API pages newest first so a long range keeps the latest 200).
  const items = useMemo(() => (orders.data?.items ?? []).slice().sort((a, b) => a.occurred_at.localeCompare(b.occurred_at) || a.folio.localeCompare(b.folio)), [orders.data]);
  const counts = useMemo(() => {
    const c = { all: items.length, pending_sample: 0, in_process: 0, to_validate: 0, validated: 0, delivered: 0, balance: 0 };
    for (const o of items) {
      if (o.state === "cancelled") continue;
      if (o.has_pending_samples) c.pending_sample++;
      if (toValidate.has(o.id)) c.to_validate++;
      else if (o.state === "in_process") c.in_process++;
      if (o.state === "reported_final" || o.state === "reported_partial") c.validated++;
      if (o.state === "closed") c.delivered++;
      if (o.balance_cents > 0) c.balance++;
    }
    return c;
  }, [items, toValidate]);

  const visible = items.filter((o) => {
    switch (filter) {
      case "pending_sample":
        return o.has_pending_samples && o.state !== "cancelled";
      case "in_process":
        return o.state === "in_process" && !toValidate.has(o.id);
      case "to_validate":
        return toValidate.has(o.id);
      case "validated":
        return o.state === "reported_final" || o.state === "reported_partial";
      case "delivered":
        return o.state === "closed";
      case "balance":
        return o.balance_cents > 0 && o.state !== "cancelled";
      default:
        return true;
    }
  });

  const sales = items.filter((o) => o.state !== "cancelled").reduce((s, o) => s + o.paid_cents, 0);

  return (
    <>
      <PageHead
        kicker={branch.name}
        title={ordersTitle(range)}
        lede={range.preset !== "any" ? `${counts.all} ${counts.all === 1 ? "solicitud" : "solicitudes"} · cobrado ${money(sales)}` : "Todas las solicitudes del historial, las más recientes primero."}
        actions={
          <>
            <Button variant="primary" size="lg" icon="plus" onClick={() => navigate("/recepcion")}>
              Nueva solicitud
            </Button>
          </>
        }
      />

      <div className="stat-row">
        <StatTile label="Todas" value={counts.all} active={filter === "all"} onClick={() => setFilter("all")} />
        <StatTile label="Muestra pendiente" value={counts.pending_sample} tone="red" active={filter === "pending_sample"} onClick={() => setFilter("pending_sample")} />
        <StatTile label="En proceso" value={counts.in_process} active={filter === "in_process"} onClick={() => setFilter("in_process")} />
        <StatTile label="Por validar" value={counts.to_validate} tone="amber" active={filter === "to_validate"} onClick={() => setFilter("to_validate")} />
        <StatTile label="Validadas" value={counts.validated} tone="green" active={filter === "validated"} onClick={() => setFilter("validated")} />
        <StatTile label="Entregadas" value={counts.delivered} active={filter === "delivered"} onClick={() => setFilter("delivered")} />
        <StatTile label="Con adeudo" value={counts.balance} tone="amber" active={filter === "balance"} onClick={() => setFilter("balance")} />
      </div>

      <div className="toolbar">
        <SearchInput icon="scan" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Folio, paciente o escanea el código…" aria-label="Buscar solicitud" />
        <DateRangeFilter value={range} onChange={setRange} allowAny />
      </div>

      <section className="card" style={{ overflow: "hidden" }}>
        {orders.loading && !orders.data ? (
          <Loading />
        ) : orders.error ? (
          <Empty icon="warning" title="No se pudo cargar" hint={orders.error} />
        ) : visible.length === 0 ? (
          <Empty icon="list" title={items.length ? "Nada con ese filtro" : "Sin solicitudes"} hint={items.length ? "Elige otro filtro." : "Registra la primera con «Nueva solicitud»."} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Folio</th>
                  <th>Paciente</th>
                  <th>Médico</th>
                  <th>Estado</th>
                  <th className="right">Total</th>
                  <th className="right">Adeudo</th>
                  <th className="right">Imprimir</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((o) => (
                  <OrderRow key={o.id} order={o} toValidate={toValidate.has(o.id)} showDate={!dates.day} onOpen={() => navigate(`/solicitudes/${o.id}`)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function StatTile({ label, value, tone, active, onClick }: { label: string; value: number; tone?: "red" | "amber" | "green"; active: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`stat ${tone ?? ""} ${active ? "active" : ""}`} onClick={onClick} aria-pressed={active}>
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </button>
  );
}

function ordersTitle(r: DateRange): string {
  switch (r.preset) {
    case "today":
      return "Solicitudes de hoy";
    case "yesterday":
      return "Solicitudes de ayer";
    case "week":
      return "Solicitudes de los últimos 7 días";
    case "month":
      return "Solicitudes de este mes";
    case "any":
      return "Solicitudes abiertas";
    case "range":
      return `Solicitudes ${describeDateRange(r)}`;
  }
}

function OrderRow({ order: o, toValidate, showDate, onOpen }: { order: Order; toValidate: boolean; showDate: boolean; onOpen: () => void }) {
  const patient = useQuery(() => get("patient", { params: { id: o.patient_id } }), [o.patient_id]);
  const chips = orderChips(o, { toValidate });
  return (
    <tr className="clickable" onClick={onOpen} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()}>
      <td className="tnum muted">{showDate ? `${fmtDate(o.occurred_at)} ${fmtTime(o.occurred_at)}` : fmtTime(o.occurred_at)}</td>
      <td>
        <span className="folio">{o.folio}</span>
      </td>
      <td>
        <span style={{ fontWeight: 600 }}>{patient.data ? `${patient.data.first_name} ${patient.data.paternal_surname} ${patient.data.maternal_surname ?? ""}`.trim() : "…"}</span>
      </td>
      <td className="muted">{o.doctor_display}</td>
      <td>
        <span className="row wrap" style={{ gap: 6 }}>
          {chips.map((c) => (
            <Chip key={c.label} tone={c.tone}>
              {c.label}
            </Chip>
          ))}
        </span>
      </td>
      <td className="right">
        <Money cents={o.total_cents} />
      </td>
      <td className="right">{o.balance_cents > 0 ? <Money cents={o.balance_cents} className="" /> : <span className="faint">—</span>}</td>
      <td className="right" style={{ whiteSpace: "nowrap" }}>
        <OrderRowPrintButtons orderId={o.id} folio={o.folio} samplesCount={o.samples_count} />
      </td>
      <td className="right">
        <Link to={`/solicitudes/${o.id}`} className="btn btn-ghost btn-icon btn-sm" aria-label={`Abrir ${o.folio}`} onClick={(e) => e.stopPropagation()}>
          <Icon name="chevronRight" />
        </Link>
      </td>
    </tr>
  );
}
